import { useState } from 'react';
import { X, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitDemoRequest } from '@/lib/api';
import { toast } from 'sonner';

export const DemoModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    company_size: '',
    channel_type: '',
    geography: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await submitDemoRequest(formData);
      setSuccess(true);
      toast.success('Demo request submitted successfully!');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      company_size: '',
      channel_type: '',
      geography: '',
      message: '',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="demo-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-2xl animate-slide-up">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {success ? (
          // Success State
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h3>
            <p className="text-slate-600 mb-6">
              Your demo request has been received. Our team will contact you within 24 hours.
            </p>
            <Button onClick={handleClose} className="bg-[#2C39B8] hover:bg-blue-900 rounded-full px-8">
              Close
            </Button>
          </div>
        ) : (
          // Form State
          <>
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Book a Demo</h2>
              <p className="text-slate-600 mt-1">See ChannelLoyalty in action. Fill out the form below.</p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-white border-slate-300"
                    data-testid="demo-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700">Work Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-white border-slate-300"
                    data-testid="demo-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company" className="text-slate-700">Company *</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-white border-slate-300"
                    data-testid="demo-company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-700">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 bg-white border-slate-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Company Size</Label>
                  <Select onValueChange={(v) => handleSelectChange('company_size', v)}>
                    <SelectTrigger className="mt-1 bg-white border-slate-300">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="1-50">1-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-1000">201-1000 employees</SelectItem>
                      <SelectItem value="1000+">1000+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700">Channel Type</Label>
                  <Select onValueChange={(v) => handleSelectChange('channel_type', v)}>
                    <SelectTrigger className="mt-1 bg-white border-slate-300">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="distributors">Distributors</SelectItem>
                      <SelectItem value="retailers">Retailers</SelectItem>
                      <SelectItem value="influencers">Influencers/Applicators</SelectItem>
                      <SelectItem value="mixed">Mixed Channel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-slate-700">Geography</Label>
                <Select onValueChange={(v) => handleSelectChange('geography', v)}>
                  <SelectTrigger className="mt-1 bg-white border-slate-300">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="india">India</SelectItem>
                    <SelectItem value="asia-pacific">Asia Pacific</SelectItem>
                    <SelectItem value="middle-east">Middle East</SelectItem>
                    <SelectItem value="europe">Europe</SelectItem>
                    <SelectItem value="americas">Americas</SelectItem>
                    <SelectItem value="global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-700">Tell us about your needs</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 bg-white border-slate-300"
                  placeholder="What challenges are you looking to solve?"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2C39B8] hover:bg-blue-900 text-white rounded-xl py-6 font-semibold"
                data-testid="demo-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <>Request Demo <ArrowRight className="ml-2" size={18} /></>
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
